import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Button, Checkbox, ErrorNote, Field, Input, Notice, Tabs } from '../../components/ui';
import { api, clearStoredToken, errorMessage } from '../../lib/api';
import { FullPageSpinner, useCustomerMe } from '../../lib/auth';

type Mode = 'signin' | 'register' | 'code';

/**
 * Customer sign-in. With OTP enabled: mobile → OTP. Otherwise: mobile + PIN, new registration with PIN,
 * or a one-time setup code from staff for customers already registered at the counter.
 */
export default function CustomerLogin() {
  const { data: me, isLoading } = useCustomerMe();
  const {
    data: cfg,
    isLoading: cfgLoading,
    isError: cfgError,
    error: cfgErr,
    refetch: refetchCfg,
  } = useQuery({ queryKey: ['customer-config'], queryFn: () => api<{ otpLogin: boolean; business: { name: string } }>('/customer/config') });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<Mode>('signin');
  const [mobile, setMobile] = useState('');
  const [pin, setPin] = useState('');
  const [code, setCode] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [name, setName] = useState('');
  const [place, setPlace] = useState('');
  const [consent, setConsent] = useState(false);

  const done = async () => {
    clearStoredToken();
    await qc.invalidateQueries({ queryKey: ['customer-me'] });
    navigate((location.state as { from?: string } | null)?.from ?? '/app', { replace: true });
  };

  const requestOtp = useMutation({ mutationFn: () => api('/customer/auth/otp/request', { body: { mobile } }), onSuccess: () => setOtpSent(true) });
  const verify = useMutation({
    mutationFn: () =>
      api('/customer/auth/otp/verify', { body: { mobile, code, pin: pin || undefined, name: name || undefined, place: place || undefined, marketingConsent: consent } }),
    onSuccess: done,
  });
  const login = useMutation({ mutationFn: () => api('/customer/auth/login', { body: { mobile, pin } }), onSuccess: done });
  const register = useMutation({
    mutationFn: () => api('/customer/auth/register', { body: { mobile, pin, name, place: place || undefined, marketingConsent: consent, setupCode: setupCode || undefined } }),
    onSuccess: done,
  });

  if (isLoading || cfgLoading) return <FullPageSpinner />;
  if (cfgError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-shell text-slate-800 text-center">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xl font-bold">!</div>
          <h2 className="text-lg font-bold text-slate-900">Unable to load app config</h2>
          <p className="text-sm text-slate-600">{errorMessage(cfgErr)}</p>
          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={() => refetchCfg()}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg text-sm cursor-pointer"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!cfg) return <FullPageSpinner />;
  if (me) return <Navigate to="/app" replace />;

  const mobileField = (
    <Field label="Mobile number">
      {(id) => <Input id={id} type="tel" inputMode="numeric" autoComplete="tel" placeholder="98765 43210" value={mobile} onChange={(e) => setMobile(e.target.value)} required />}
    </Field>
  );
  const pinField = (label: string) => (
    <Field label={label} hint="4–6 digits">
      {(id) => <Input id={id} type="password" inputMode="numeric" autoComplete="current-password" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} required />}
    </Field>
  );
  const profileFields = (
    <>
      <Field label="Your name">{(id) => <Input id={id} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required />}</Field>
      <Field label="Place (town / area)">{(id) => <Input id={id} value={place} onChange={(e) => setPlace(e.target.value)} />}</Field>
      <Checkbox label="Send me offers and event updates" description="You can turn this off any time in your profile." checked={consent} onChange={setConsent} />
    </>
  );

  return (
    <div className="flex min-h-screen flex-col bg-shell px-4 pt-[max(2rem,env(safe-area-inset-top))]">
      <div className="mx-auto w-full max-w-sm">
        <p className="text-3xl font-bold tracking-tight text-white">
          {cfg.business.name}
        </p>
        <p className="mb-6 mt-1 text-sm text-slate-300">Your wallet, rewards and visits at {cfg.business.name}</p>
        <div className="rounded-2xl bg-white p-5 shadow-xl">
          {cfg.otpLogin ? (
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                otpSent ? verify.mutate() : requestOtp.mutate();
              }}
            >
              {mobileField}
              {otpSent && (
                <>
                  <Field label="6-digit code" hint="Sent by SMS to your mobile">
                    {(id) => <Input id={id} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required />}
                  </Field>
                  <details className="text-sm">
                    <summary className="cursor-pointer text-slate-600">New here? Add your details</summary>
                    <div className="mt-3 flex flex-col gap-3">{profileFields}</div>
                  </details>
                </>
              )}
              <ErrorNote error={requestOtp.error || verify.error} />
              <Button type="submit" size="lg" loading={requestOtp.isPending || verify.isPending}>
                {otpSent ? 'Verify & continue' : 'Send code'}
              </Button>
            </form>
          ) : (
            <>
              <Tabs<Mode>
                value={mode}
                onChange={setMode}
                tabs={[
                  { value: 'signin', label: 'Sign in' },
                  { value: 'register', label: 'Register' },
                  { value: 'code', label: 'Setup code' },
                ]}
              />
              <form
                className="flex flex-col gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (mode === 'signin') login.mutate();
                  else if (mode === 'register') register.mutate();
                  else verify.mutate();
                }}
              >
                {mobileField}
                {mode === 'signin' && pinField('PIN')}
                {mode === 'register' && (
                  <>
                    {profileFields}
                    <Field label="Setup code from the counter" hint="6 digits. Ask the counter or the gate for it: it proves this mobile is yours.">
                      {(id) => (
                        <Input
                          id={id}
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          value={setupCode}
                          onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ''))}
                          required
                        />
                      )}
                    </Field>
                    {pinField('Choose a PIN')}
                  </>
                )}
                {mode === 'code' && (
                  <>
                    <Notice>Registered at the counter? Ask staff for a one-time setup code, then choose your PIN.</Notice>
                    <Field label="Setup code">
                      {(id) => <Input id={id} inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required />}
                    </Field>
                    {pinField('Choose a PIN')}
                  </>
                )}
                <ErrorNote error={login.error || register.error || verify.error} />
                <Button type="submit" size="lg" loading={login.isPending || register.isPending || verify.isPending}>
                  {mode === 'signin' ? 'Sign in' : mode === 'register' ? 'Create account' : 'Activate'}
                </Button>
              </form>
            </>
          )}
        </div>
        <p className="mt-4 text-center text-xs text-slate-400">Staff? <a className="underline" href="/admin/login">Sign in here</a></p>
      </div>
    </div>
  );
}
